<template>
  <div
    class="
      absolute
      top-0
      left-0
      w-full
      h-full
      bg-black
      z-20
      flex flex-col
      items-center
      justify-center
      p-8
    "
  >
    <ShieldExclamationIcon class="text-white w-12 h-12 mb-3" />
    <p class="text-white text-center font-bold text-xl mb-7">Your stream is turned OFF</p>

    <p class="text-white text-center font-bold text-3xl mb-7">{{ formatedDuration }}</p>

    <div class="details">
      <p class="text-white text-center font-bold mb-7">
        You have 90 seconds to restore patient
        <br />
        confidentiality and turn your stream back ON.
      </p>
      <p class="text-white text-center mb-7">
        After 90 seconds you will automatically leave the live consultation and be taken to the
        dashboard.
      </p>
    </div>

    <ViceButton dark :disabled="!turnOnAvailable" icon="ShieldCheckIcon" @click="turnStreamOn">
      Turn stream ON
    </ViceButton>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { ShieldExclamationIcon } from '@heroicons/vue/outline';
import { Inertia } from '@inertiajs/inertia';
import useTimer from '@/Composables/useTimer';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import {
  STREAM_TURNED_OFF_DURATION,
  TIMER_ACTION,
  USER_SESSION_ACTIVITY_KEY,
  USER_SESSION_ON_HOLD_KEY,
} from '@/Utils/vice-constants';

export default {
  name: 'SessionOnHoldOverlay',
  components: {
    ViceButton,
    ShieldExclamationIcon,
  },

  setup() {
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const onHoldForKey = `${USER_SESSION_ON_HOLD_KEY}`;
    const turnOnAvailable = computed(() => multiCameraDevicesStore.isMediaTrackReady);
    const duration = () => {
      const storedDuration = localStorage.getItem(onHoldForKey);
      return storedDuration && storedDuration > 0
        ? Number(storedDuration) - 1000
        : STREAM_TURNED_OFF_DURATION;
    };

    const { startTimer, formatedDuration, stopTimer, rawTimeLeft } = useTimer(
      duration(),
      TIMER_ACTION.DECREASE,
    );

    localStorage.removeItem(onHoldForKey);

    onMounted(() => {
      startTimer();
      window.addEventListener('beforeunload', () => {
        if (sessionStore.isPutOnHold && rawTimeLeft.value > 0) {
          localStorage.setItem(onHoldForKey, rawTimeLeft.value);
        }
      });
    });

    onUnmounted(() => {
      stopTimer();
      localStorage.removeItem(onHoldForKey);
      window.removeEventListener('beforeunload', () => {}, true);
    });

    watch(rawTimeLeft, (currentTimeLeft) => {
      if (currentTimeLeft <= 0) {
        stopTimer();

        // Cache user information indicating they have been
        // kicked out of the session for not turning on their
        // camera after the 5 minutes countdown
        localStorage.setItem(
          `${USER_SESSION_ACTIVITY_KEY}_${sessionStore.sessionId}`,
          JSON.stringify({
            kickOutTimestamp: Date.now(),
            userId: sessionStore.authUser.id,
            isKickedOutOfConsultation: true,
            sessionId: sessionStore.sessionId,
          }),
        );
        window.removeEventListener('beforeunload', () => {}, true);
        localStorage.removeItem(onHoldForKey);

        // Take the user to the dashboard
        Inertia.visit(route('dashboard'));
      }
    });

    const turnStreamOn = () => {
      sessionStore.isPutOnHold = false;
      localStorage.removeItem(onHoldForKey);
    };

    return {
      turnStreamOn,
      turnOnAvailable,
      formatedDuration,
    };
  },
};
</script>

<style scoped>
.details {
  max-width: 415px;
}
</style>
